import React, { useEffect, useState } from "react";
import { QrCode, Paperclip, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { useApp, formatBRL } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const hojeISO = () => new Date().toISOString().slice(0, 10);

const lerArquivo = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(fr.result);
  fr.onerror = reject;
  fr.readAsDataURL(file);
});

export const BaixaPixDialog = ({ fatura, residente, open, onClose }) => {
  const { baixaPixFatura } = useApp();
  const { toast } = useToast();
  const [data, setData] = useState(hojeISO());
  const [valor, setValor] = useState("");
  const [obs, setObs] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) { setData(hojeISO()); setValor(fatura ? String(fatura.total.toFixed(2)) : ""); setObs(""); setArquivo(null); }
  }, [open, fatura?.key, fatura?.total]);

  const confirmar = async () => {
    const v = Number(String(valor).replace(",", "."));
    if (!data) return toast({ title: "Informe a data do pagamento", variant: "destructive" });
    if (!(v > 0)) return toast({ title: "Informe um valor válido", variant: "destructive" });
    if (arquivo && arquivo.size > 15 * 1024 * 1024) return toast({ title: "Comprovante acima de 15 MB", variant: "destructive" });
    setSalvando(true);
    try {
      const payload = { residenteId: fatura.residenteId, mes: fatura.mes, dataPagamento: data, valor: v, observacao: obs };
      if (arquivo) { payload.comprovanteBase64 = await lerArquivo(arquivo); payload.comprovanteNome = arquivo.name; }
      await baixaPixFatura(payload);
      toast({ title: "Fatura baixada via Pix", description: `${residente?.nome || ""} · ${formatBRL(v)}${arquivo ? " · comprovante anexado" : ""}` });
      onClose();
    } catch (e) {
      toast({ title: "Não foi possível registrar", description: e?.response?.data?.detail || "Erro", variant: "destructive" });
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="baixa-pix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-emerald-600" /> Baixa por Pix · {residente?.nome}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-500">Fatura {fatura?.mes} · em aberto {fatura ? formatBRL(fatura.total) : ""}</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="text-xs font-medium text-slate-600">Data do pagamento</span>
              <input type="date" className={inputCls} value={data} max={hojeISO()} onChange={(e) => setData(e.target.value)} data-testid="baixa-pix-data" /></label>
            <label className="space-y-1"><span className="text-xs font-medium text-slate-600">Valor recebido (R$)</span>
              <input type="number" step="0.01" min="0" className={inputCls} value={valor} onChange={(e) => setValor(e.target.value)} data-testid="baixa-pix-valor" /></label>
          </div>
          <label className="space-y-1 block"><span className="text-xs font-medium text-slate-600">Comprovante (PDF, PNG ou JPG · opcional)</span>
            <input type="file" accept="application/pdf,image/png,image/jpeg" className="block w-full text-xs text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)} data-testid="baixa-pix-arquivo" /></label>
          {arquivo && <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="baixa-pix-arquivo-nome"><Paperclip className="w-3.5 h-3.5" /> {arquivo.name} ({(arquivo.size / 1024).toFixed(0)} KB) — será salvo nos documentos do residente como "comprovante"</p>}
          <label className="space-y-1 block"><span className="text-xs font-medium text-slate-600">Observação (opcional)</span>
            <input className={inputCls} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex.: Pix recebido do filho João" data-testid="baixa-pix-obs" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="baixa-pix-cancelar">Cancelar</Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={salvando} onClick={confirmar} data-testid="baixa-pix-confirmar">
            <CheckCircle2 className="w-4 h-4" /> {salvando ? "Registrando..." : "Confirmar baixa via Pix"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
